/**
 * d-collection-sort-availability.js
 *
 * Ordena os produtos da coleção colocando disponíveis antes dos esgotados,
 * buscando todas as páginas da coleção e construindo uma paginação virtual
 * no cliente (sem recarregar a página).
 *
 * ── Como incluir ────────────────────────────────────────────────────────────
 * Adicione a tag abaixo no template de coleção (ex: sections/main-collection-product-grid.liquid):
 *
 *   <script src="{{ 'd-collection-sort-availability.js' | asset_url }}" defer></script>
 *
 * ── Pré-requisitos no HTML ───────────────────────────────────────────────────
 * O script depende de três elementos no DOM:
 *
 *   1. Grid de produtos com id="product-grid" e os data attributes:
 *        data-id="{{ section.id }}"
 *        data-per-page="{{ section.settings.products_per_page }}"
 *        data-total-pages="{{ paginate.pages }}"
 *
 *   2. Container externo com id="ProductGridContainer" — observado via
 *      MutationObserver para detectar re-renders do Dawn após filtros/sort AJAX.
 *
 *   3. Wrapper de paginação com classe "pagination-wrapper" — onde a paginação
 *      virtual será injetada.
 *
 * ── Marcação de disponibilidade nos cards ────────────────────────────────────
 * Cada <li> do grid deve ter o data attribute abaixo para que o script saiba
 * quais produtos estão esgotados:
 *
 *   data-product-available="{{ product.available }}"
 *   (Shopify renderiza como "true" ou "false")
 *
 * ── Contador de produtos (opcional) ─────────────────────────────────────────
 * Para exibir "Mostrando X–Y de Z produtos", adicione o atributo em qualquer
 * elemento da página:
 *
 *   <span data-collection-count></span>
 *
 * ── Constantes ajustáveis ────────────────────────────────────────────────────
 * Se o tema mudar os IDs/seletores padrão do Dawn, edite as constantes no topo
 * do script (GRID_ID, GRID_CONTAINER_ID, PAGINATION_SEL, MAX_PAGES).
 *
 * ── Comportamento ────────────────────────────────────────────────────────────
 * - Coleção com 1 página: reordena os cards diretamente no DOM (sem fetch).
 * - Coleção com múltiplas páginas: faz fetch paralelo de todas as páginas,
 *   une os cards, ordena e constrói paginação virtual client-side.
 * - Compatível com filtros e ordenação AJAX do Dawn (detecta re-renders via
 *   MutationObserver e reinicia o sort automaticamente).
 * - Compatível com navegação via browser back/forward (popstate).
 * - Em caso de erro no fetch, cai no reorder de DOM como fallback.
 */

(function () {
  'use strict';

  // ─── Selectors (adapt these if the theme changes) ──────────────────────────
  var GRID_ID           = 'product-grid';
  var GRID_CONTAINER_ID = 'ProductGridContainer';
  var PAGINATION_SEL    = '.pagination-wrapper';
  var MAX_PAGES         = 20;

  // ─── State ─────────────────────────────────────────────────────────────────
  var state = {
    cards        : [],   // { html, soldOut }[]
    promoCard    : null, // promo card kept on page 1 only
    promoPosition: 0,   // 1-based grid position from settings
    perPage      : 8,
    sectionId    : '',
    totalPages   : 1,
    initialized  : false,
    fetching     : false,
    generation   : 0,
    arrowPrevHtml: '',
    arrowNextHtml: '',
  };

  // ─── Read metadata from #product-grid data attributes ─────────────────────
  function readMeta() {
    var el = document.getElementById(GRID_ID);
    if (!el) return null;
    return {
      sectionId : el.dataset.id,
      perPage   : parseInt(el.dataset.perPage, 10)   || 24,
      totalPages: parseInt(el.dataset.totalPages, 10) || 1,
    };
  }

  // ─── URL builder — preserves existing filter/sort params ──────────────────
  function buildUrl(page, sectionId) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', page);
    params.set('section_id', sectionId);
    return window.location.pathname + '?' + params.toString();
  }

  // ─── Fetch all server pages in parallel ───────────────────────────────────
  function fetchAllPages(sectionId, totalPages) {
    var count = Math.min(totalPages, MAX_PAGES);
    var fetches = [];
    for (var i = 1; i <= count; i++) {
      fetches.push(
        fetch(buildUrl(i, sectionId)).then(function (r) { return r.text(); })
      );
    }
    return Promise.all(fetches);
  }

  // ─── Extract <li> cards from fetched HTML ─────────────────────────────────
  function extractCards(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('#' + GRID_ID + ' > li')).map(function (li) {
      return {
        html         : li.outerHTML,
        soldOut      : li.dataset.productAvailable === 'false',
        isPromo      : li.dataset.promoCard === 'true',
        promoPosition: parseInt(li.dataset.promoPosition, 10) || 0,
      };
    });
  }

  // ─── Sort: available first, sold-out last; promo kept separately ──────────
  function sortCards(cards) {
    var promos    = cards.filter(function (c) { return c.isPromo; });
    var products  = cards.filter(function (c) { return !c.isPromo; });
    // Keep only the first promo card encountered (one per collection)
    state.promoCard     = promos.length > 0 ? promos[0] : null;
    state.promoPosition = state.promoCard ? state.promoCard.promoPosition : 1;
    var available = products.filter(function (c) { return !c.soldOut; });
    var soldOut   = products.filter(function (c) { return c.soldOut; });
    return available.concat(soldOut);
  }

  // ─── Capture arrow SVGs from the server-rendered pagination ───────────────
  function captureArrows() {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (!wrapper) return;
    // Dawn's pagination uses .pagination__item--next for "prev" arrow and vice-versa
    var prevEl = wrapper.querySelector('.pagination__item--next .svg-wrapper');
    var nextEl = wrapper.querySelector('.pagination__item--prev .svg-wrapper');
    if (prevEl) state.arrowPrevHtml = prevEl.outerHTML;
    if (nextEl) state.arrowNextHtml = nextEl.outerHTML;
  }

  // ─── Update count text elements ───────────────────────────────────────────
  function updateCount(pageNum) {
    var total = state.cards.length;
    var start = (pageNum - 1) * state.perPage + 1;
    var end   = Math.min(pageNum * state.perPage, total);
    var text  = 'Mostrando ' + start + '\u2013' + end + ' de ' + total + ' produtos';
    document.querySelectorAll('[data-collection-count]').forEach(function (el) {
      el.textContent = text;
    });
  }

  // ─── Re-execute <script> tags lost when injected via innerHTML ────────────
  // Cards carry an inline <script> that registers their discoAlpine.data()
  // component. Browsers never run <script> elements inserted via innerHTML,
  // so without this, cards from any page fetched via JS never get their
  // Alpine component registered — disco-text bindings (price, etc.) fail.
  function executeScripts(container) {
    Array.from(container.querySelectorAll('script')).forEach(function (oldScript) {
      var newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(function (attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  // ─── Render a page slice into the grid ────────────────────────────────────
  function renderPage(pageNum) {
    var grid = document.getElementById(GRID_ID);
    if (!grid || state.cards.length === 0) return;

    var start = (pageNum - 1) * state.perPage;
    var slice = state.cards.slice(start, start + state.perPage);
    if (slice.length === 0) return;

    var items = slice.map(function (c) { return c.html; });
    if (pageNum === 1 && state.promoCard) {
      var pos = Math.max(0, Math.min(state.promoPosition - 1, items.length));
      items.splice(pos, 0, state.promoCard.html);
    }
    grid.innerHTML = items.join('');
    executeScripts(grid);

    // Re-initialize Alpine so disco-cloak/disco-show are processed on inserted cards
    if (window.discoAlpine && typeof discoAlpine.initTree === 'function') {
      discoAlpine.initTree(grid);
    }
    // Fallback: strip disco-cloak so cards are never permanently hidden
    grid.querySelectorAll('[disco-cloak]').forEach(function (el) {
      el.removeAttribute('disco-cloak');
    });

    // Reset animation order on inserted items
    Array.from(grid.children).forEach(function (li, i) {
      li.style.setProperty('--animation-order', i + 1);
      li.style.opacity = '1';
      li.removeAttribute('reveal');
    });
  }

  // ─── Smart page list with ellipsis ───────────────────────────────────────
  function getPageRange(cur, total) {
    if (total <= 7) {
      var all = [];
      for (var i = 1; i <= total; i++) all.push(i);
      return all;
    }
    var result = [1];
    if (cur > 3) result.push('...');
    var from = Math.max(2, cur - 1);
    var to   = Math.min(total - 1, cur + 1);
    for (var i = from; i <= to; i++) result.push(i);
    if (cur < total - 2) result.push('...');
    result.push(total);
    return result;
  }

  // ─── Build virtual pagination using section CSS classes ───────────────────
  function buildPagination(currentPage) {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (!wrapper) return;

    var totalPages = Math.ceil(state.cards.length / state.perPage);

    if (totalPages <= 1) {
      wrapper.innerHTML = '';
      return;
    }

    var SVG_PREV = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var SVG_NEXT = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    var html = '<nav class="d-mc-pagination__nav" aria-label="Paginação">';

    // Prev
    if (currentPage > 1) {
      html += '<a href="#" class="d-mc-pag-btn d-mc-pag-arrow" data-vs-page="' + (currentPage - 1) + '" aria-label="Página anterior">' + SVG_PREV + '</a>';
    }

    // Numbered buttons
    var pages = getPageRange(currentPage, totalPages);
    for (var i = 0; i < pages.length; i++) {
      var p = pages[i];
      if (p === '...') {
        html += '<span class="d-mc-pag-ellipsis">…</span>';
      } else {
        var isCur = p === currentPage;
        html += '<a href="#"'
          + ' class="d-mc-pag-btn' + (isCur ? ' d-mc-pag-btn--current' : '') + '"'
          + ' data-vs-page="' + p + '"'
          + ' aria-label="Página ' + p + '"'
          + (isCur ? ' aria-current="page"' : '')
          + '>' + p + '</a>';
      }
    }

    // Next
    if (currentPage < totalPages) {
      html += '<a href="#" class="d-mc-pag-btn d-mc-pag-arrow" data-vs-page="' + (currentPage + 1) + '" aria-label="Próxima página">' + SVG_NEXT + '</a>';
    }

    html += '</nav>';

    wrapper.innerHTML = html;
    wrapper.dataset.page = currentPage;
  }

  // ─── Navigate to a virtual page ───────────────────────────────────────────
  function navigateTo(pageNum) {
    renderPage(pageNum);
    buildPagination(pageNum);
    updateCount(pageNum);

    var url = new URL(window.location.href);
    if (pageNum === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', pageNum);
    }
    history.pushState({ vsPage: pageNum }, '', url.toString());

    // Scroll to top of grid container
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (container) {
      var top = container.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  // ─── Attach click handler to the virtual pagination ───────────────────────
  function attachPaginationListeners() {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (!wrapper) return;
    wrapper.removeEventListener('click', onPaginationClick);
    wrapper.addEventListener('click', onPaginationClick);
  }

  function onPaginationClick(e) {
    var link = e.target.closest('a[data-vs-page]');
    if (!link) return;
    e.preventDefault();
    var page = parseInt(link.dataset.vsPage, 10);
    if (!isNaN(page)) navigateTo(page);
  }

  // ─── In-page DOM reorder (fallback for single-page collections) ───────────
  function reorderDOM() {
    var grid = document.getElementById(GRID_ID);
    if (!grid) return;
    var items     = Array.from(grid.children);
    var promos    = items.filter(function (li) { return li.dataset.promoCard === 'true'; });
    var available = items.filter(function (li) { return li.dataset.productAvailable !== 'false' && li.dataset.promoCard !== 'true'; });
    var soldOut   = items.filter(function (li) { return li.dataset.productAvailable === 'false'; });
    available.concat(soldOut).concat(promos).forEach(function (li) { grid.appendChild(li); });
  }

  // ─── Virtual sort orchestrator ────────────────────────────────────────────
  function initVirtualSort() {
    var meta = readMeta();
    if (!meta) return;

    state.sectionId = meta.sectionId;
    state.perPage   = meta.perPage;
    state.totalPages = meta.totalPages;

    // Single-page collection: just reorder in DOM, no fetch needed
    if (meta.totalPages <= 1) {
      reorderDOM();
      return;
    }

    var gen = ++state.generation;
    state.fetching    = false;
    state.initialized = false;
    state.fetching    = true;

    // Capture arrows before we replace server-rendered pagination
    captureArrows();

    // Hide grid to prevent flash of unsorted content
    var grid = document.getElementById(GRID_ID);
    if (grid) grid.style.visibility = 'hidden';

    fetchAllPages(state.sectionId, state.totalPages)
      .then(function (pages) {
        if (gen !== state.generation) return; // Stale — a newer sort was triggered

        var all = [];
        pages.forEach(function (html) {
          all = all.concat(extractCards(html));
        });

        state.cards       = sortCards(all);
        state.fetching    = false;
        state.initialized = true;

        renderPage(1);
        buildPagination(1);
        updateCount(1);
        attachPaginationListeners();

        if (grid) grid.style.visibility = '';
      })
      .catch(function (err) {
        console.warn('[sort-availability] Fetch failed, falling back to in-page sort.', err);
        if (gen !== state.generation) return;
        state.fetching = false;
        if (grid) grid.style.visibility = '';
        reorderDOM();
      });
  }

  // ─── MutationObserver — detect Dawn's facets/sort AJAX updates ────────────
  var _debounceTimer = null;

  function watchForUpdates() {
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (!container) return;

    var observer = new MutationObserver(function (mutations) {
      // Only react when #product-grid itself is (re)inserted into the DOM
      var gridInserted = mutations.some(function (m) {
        return Array.from(m.addedNodes).some(function (n) {
          if (n.nodeType !== 1) return false;
          return n.id === GRID_ID
            || (typeof n.querySelector === 'function' && !!n.querySelector('#' + GRID_ID));
        });
      });

      if (!gridInserted || state.fetching) return;

      // Debounce: wait for URL (history.pushState) to update after DOM change
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(function () {
        state.cards       = [];
        state.initialized = false;
        initVirtualSort();
      }, 150);
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  // ─── Handle browser back/forward navigation ───────────────────────────────
  window.addEventListener('popstate', function (e) {
    if (state.initialized && e.state && e.state.vsPage) {
      renderPage(e.state.vsPage);
      buildPagination(e.state.vsPage);
    }
  });

  // ─── Boot ─────────────────────────────────────────────────────────────────
  function boot() {
    if (!document.getElementById(GRID_ID)) return;
    initVirtualSort();
    watchForUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
